import type { DocumentTokens } from './documentScanner';
import type { SupportedTags } from '@/types/SupportedTags';

/**
 * Diffs the tokens the scanner found in the document against the mapper's
 * declared inventory. Emits three lists so the UI can group + label them.
 *
 * Column-tag scoping caveat: this validator treats a column tag as valid if
 * ANY supported region declares it. A wrongly-scoped column (e.g. `{{Code}}`
 * outside a region, or inside a region that doesn't declare that column)
 * won't be flagged. Region-scoped validation would require walking the doc
 * structure, deferred to a follow-up.
 */

export interface ValidationResult {
  unknownScalars: string[];
  unknownRegions: string[];
  unknownColumns: string[];
  isClean: boolean;
}

export function validateTags(
  found: DocumentTokens,
  supported: SupportedTags,
): ValidationResult {
  const supportedScalars = new Set(supported.fieldTags);
  const supportedRegions = new Set(supported.groups.map((g) => g.name));

  // Union of every column across every supported region — column tags are only
  // flagged as unknown when they aren't declared by ANY region for this source
  // type. Bare Word text like a person's name would also match this shape, so
  // we accept some false negatives to avoid noisy false positives.
  const supportedColumns = new Set<string>();
  for (const group of supported.groups) {
    for (const column of group.columns) {
      supportedColumns.add(column);
    }
  }

  const unknownScalars = found.scalarTags.filter((tag) => !supportedScalars.has(tag));
  const unknownRegions = found.regionNames.filter((name) => !supportedRegions.has(name));
  const unknownColumns = found.columnTags.filter((col) => !supportedColumns.has(col));

  return {
    unknownScalars,
    unknownRegions,
    unknownColumns,
    isClean:
      unknownScalars.length === 0
      && unknownRegions.length === 0
      && unknownColumns.length === 0,
  };
}
